import { QueueOptions, IngestEvent } from './types';
import { byteSizeOfJson, sleep } from './utils';
import { sendBatchBeacon, sendBatchFetch } from './sender';

export interface EventQueue {
  enqueue: (evt: IngestEvent) => void;
  flush: () => Promise<void>;
  stop: () => void;
}

interface InternalState {
  sending: boolean;
  timer: any;
}

export function createQueue(opts: QueueOptions): EventQueue {
  const state: InternalState = { sending: false, timer: null };
  const queue: IngestEvent[] = [];

  function schedule() {
    if (state.timer) return;
    state.timer = setInterval(() => {
      void flush();
    }, opts.flushIntervalMs);
    // page lifecycle
    if (typeof window !== 'undefined') {
      const flushOnPageHide = () => {
        // Prefer fetch keepalive to include headers; sendBeacon cannot add headers.
        tryFlushOnUnload();
      };
      window.addEventListener('pagehide', flushOnPageHide);
      window.addEventListener('beforeunload', flushOnPageHide);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushOnPageHide();
      });
    }
  }

  async function tryFlushOnUnload() {
    if (queue.length === 0) return;
    const batches = buildBatches();
    // Try sendBeacon first for small payloads; if not possible, do fetch keepalive.
    for (const batch of batches) {
      const body = { events: batch };
      const bytes = byteSizeOfJson(body);
      let usedBeacon = false;
      // Only attempt beacon for small bodies (~60KB) to avoid silent drop
      if (bytes < 60 * 1024) {
        usedBeacon = sendBatchBeacon(batch, {
          apiKey: opts.apiKey,
          projectId: opts.projectId,
          endpointBase: opts.endpointBase,
        });
      }
      if (!usedBeacon) {
        try {
          await sendBatchFetch(batch, {
            apiKey: opts.apiKey,
            projectId: opts.projectId,
            endpointBase: opts.endpointBase,
          }, true);
        } catch {
          // ignore
        }
      }
    }
    // Clear queue regardless; page is closing.
    queue.length = 0;
  }

  function buildBatches(): IngestEvent[][] {
    const batches: IngestEvent[][] = [];
    let idx = 0;
    while (idx < queue.length) {
      const batch: IngestEvent[] = [];
      while (idx < queue.length && batch.length < opts.maxBatchSize) {
        const candidate = queue[idx];
        const candidateBatch = [...batch, candidate];
        const size = byteSizeOfJson({ events: candidateBatch });
        if (size > opts.maxQueueBytes) break;
        batch.push(candidate);
        idx++;
      }
      if (batch.length === 0) {
        // Single item too large, drop it with a warning
        // Attempt to skip this event to avoid deadlock
        console.warn('[kromemo] Dropping oversized event');
        idx++;
        continue;
      }
      batches.push(batch);
    }
    return batches;
  }

  async function flush(): Promise<void> {
    if (state.sending) return;
    if (queue.length === 0) return;
    state.sending = true;
    try {
      const batches = buildBatches();
      // Remove items to be sent from queue upfront to avoid duplicates in retry cycles
      let toSendCount = 0;
      for (const b of batches) toSendCount += b.length;
      const sendingSlice = queue.splice(0, toSendCount);

      for (const batch of batches) {
        let attempt = 0;
        const maxAttempts = Math.max(1, opts.retryMaxAttempts);
        // retry policy: only for 5xx or network errors
        // Exponential backoff: 500ms, 1s, 2s
        // Note: if last attempt fails, we drop batch (no requeue), per typical telemetry behavior
        while (true) {
          try {
            const res = await sendBatchFetch(batch, {
              apiKey: opts.apiKey,
              projectId: opts.projectId,
              endpointBase: opts.endpointBase,
            });
            if (res.ok) {
              break; // success
            }
            // 4xx -> do not retry
            if (res.status >= 400 && res.status < 500) {
              break;
            }
            // 5xx -> retry
            attempt++;
            if (attempt >= maxAttempts) break;
            await sleep(Math.pow(2, attempt - 1) * 500);
          } catch (e) {
            attempt++;
            if (attempt >= maxAttempts) break;
            await sleep(Math.pow(2, attempt - 1) * 500);
          }
        }
      }
    } finally {
      state.sending = false;
    }
  }

  function enqueue(evt: IngestEvent) {
    queue.push(evt);
  }

  function stop() {
    if (state.timer) clearInterval(state.timer);
    state.timer = null;
  }

  schedule();

  return { enqueue, flush, stop };
}
